import React, { useState, useEffect, useMemo } from 'react'
import { Link, withRouter } from 'react-router-dom'
import AddIcon from '@material-ui/icons/Add'
import { Grid } from '@material-ui/core'

import { ClientCard } from 'Components/Clients/ClientCard'
import { ClientsWrapper } from 'Components/Clients/ClientsWrapper'
import { PageTitle } from 'Components/PageTitle'
import { LayoutDashboard } from 'Components/Layout/Dashboard'
import { HideWhenBlocked } from 'Containers/UIBlock/HideWhenBlocked'
import { STATUS_FILTER } from '../../Reducers/Clients/const'
import ClientsIcon from 'Assets/Images/icons/clients'
import { globalSort } from '../../shared/libraries/sort'
import { getClients as getClientsGraphQL } from '../../Module/graphQL/index'
import LoadingIndicator from '../../Components/LoadingIndicator/LoadingIndicator'
import useGraphQLFetch from '../../Utils/Hooks/useGraphQLFetch'
import { normalizeGraphQLConnection } from '../../shared/graphql/utils'
import Pagination from '../../Components/FilterAndPaginationComponent/Pagination'
import { isDefined } from '../../shared/libraries/utils/object'
import StyledIconButton from '../../Components/Form/StyledIconButton'
import { FilterSelect } from 'Components/Filter/FilterSelect'
import StyledBreadcrumbs from '../../Components/StyledBreadcrumbs'

const Clients = props => {
  const { countries, match } = props
  const { url } = match
  const [filters, setFilters] = useState({
    status: STATUS_FILTER.ACTIVE,
    country: 'all',
    source: 'all',
  })
  const [offsetRecordsStart, setOffsetRecordsStart] = useState(0)
  const [resultsPerPage, setResultsPerPage] = useState(50)

  const { data, isLoading, refetch } = useGraphQLFetch({
    fetchFn: args => {
      const statuses = []

      if ([STATUS_FILTER.ACTIVE, STATUS_FILTER.ALL].includes(filters.status)) {
        statuses.push('ACTIVE')
      }
      if (
        [STATUS_FILTER.ARCHIVED, STATUS_FILTER.ALL].includes(filters.status)
      ) {
        statuses.push('DELETED')
      }

      return getClientsGraphQL({
        statuses,
        countryIds: countries
          .filter(country => filters.country.includes(country.name))
          .map(country => country.id),
        first: resultsPerPage,
        ...args,
      })
    },
    mapResponse: ({ clients }) => ({
      clients: clients,
      pageInfo: clients.pageInfo,
      totalCount: clients.totalCount,
    }),
    defaultData: { clients: { edges: [] } },
  })

  useEffect(() => {
    refetch({
      skip: offsetRecordsStart,
      first: resultsPerPage,
    })
  }, [filters, offsetRecordsStart, resultsPerPage])

  const statusFilterOptions = [
    { value: STATUS_FILTER.ACTIVE, label: 'Active' },
    { value: STATUS_FILTER.ARCHIVED, label: 'Archived' },
    { value: STATUS_FILTER.ALL, label: 'All' },
  ]

  const countryFilterOptions = useMemo(
    () =>
      countries.map(({ name }) => ({
        value: name,
        label: name,
      })),
    [countries],
  )

  const sortedClients = globalSort({
    data: normalizeGraphQLConnection(data.clients),
    sortingKey: 'name',
  })

  const onOffsetChange = offset => {
    if (offset < 0) offset = 0
    if (offset > data.totalCount) return

    setOffsetRecordsStart(offset)
  }
  const onChangeFilter = (key, value) => {
    setFilters(filters => ({ ...filters, [key]: value }))
  }
  return (
    <LayoutDashboard>
      <HideWhenBlocked>
        <ClientsWrapper>
          <StyledBreadcrumbs
            icon={<ClientsIcon style={{ width: 24 }} />}
            breadcrumbs={[{ label: 'Clients' }]}
          />
          <PageTitle>Clients</PageTitle>

          <Link id="add-client-button" to={`${url}/add`}>
            <StyledIconButton>
              <AddIcon />
            </StyledIconButton>
          </Link>
          <Grid container spacing={3}>
            <Grid item>
              <FilterSelect
                label="Status"
                options={statusFilterOptions}
                value={filters.status}
                onChange={(e, index, value) => onChangeFilter('status', value)}
                sorted={true}
              />
            </Grid>
            <Grid item>
              <FilterSelect
                label="Country"
                options={[
                  { value: 'all', label: 'All' },
                  ...countryFilterOptions,
                ]}
                value={filters.country}
                onChange={(e, index, value) => onChangeFilter('country', value)}
                sorted={true}
              />
            </Grid>
          </Grid>
          {isLoading && <LoadingIndicator />}
          <Grid container spacing={3}>
            {sortedClients.map((client, index) => {
              return (
                <Grid item xs={12} md={6} lg={3} key={index}>
                  <ClientCard
                    client={client}
                    editPath={`${props.match.url}/${client.id}/edit`}
                    onClickHandler={props.history.push}
                    viewPath={`${props.match.url}/${client.id}`}
                  />
                </Grid>
              )
            })}
          </Grid>
          <Pagination
            total={
              isDefined(data.totalCount)
                ? data.totalCount
                : sortedClients.length
            }
            offset={isDefined(offsetRecordsStart) ? offsetRecordsStart : 0}
            resultsPerPage={
              isDefined(resultsPerPage) ? resultsPerPage : sortedClients.length
            }
            onOffsetChange={onOffsetChange}
            setResultsPerPage={setResultsPerPage}
          />
        </ClientsWrapper>
      </HideWhenBlocked>
    </LayoutDashboard>
  )
}

export default withRouter(Clients)
